import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { api, ApiError, fieldIssues } from '../api';
import { trapTab } from '../ui/focus';
import type { Role } from './types';
import { DialogLayer } from '../ui/Dialog';
import { useUnsavedGuard } from '../ui/unsaved';

export const ROLES_KEY = ['admin', 'roles'] as const;

function ErrorLine({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return error ? <p className="alert" role="alert">Nie udało się zapisać</p> : null;
  const issues = fieldIssues(error);
  return (
    <p className="alert" role="alert">
      {issues.length > 0 ? issues.map((i) => i.message).join(' ') : error.status === 409 ? 'Ktoś już zmienił tę rolę. Zamknij okno i spróbuj ponownie.' : error.message}
    </p>
  );
}

function DialogShell({ titleId, title, onClose, onSubmit, busy, children }: { titleId: string; title: string; onClose: () => void; onSubmit: () => void; busy?: boolean; children: React.ReactNode }) {
  // A portal: the dialog also opens from inside the user form, and forms cannot nest.
  return createPortal(
    <div className="dialog-backdrop">
      <form
        className="dialog panel form"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-feature-id="identity.roles.manage"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          e.stopPropagation(); // React events bubble through portals: never submit the form underneath
          onSubmit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id={titleId}>{title}</h2>
        {children}
      </form>
    </div>,
    document.body,
  );
}

/**
 * "Nowa rola" (name + "Skopiuj uprawnienia z") or "Zmień nazwę roli" (with `role`). Used on the permissions
 * screen and, for a new role, straight from the user form. `onSaved` gets the saved role.
 */
export function RoleFormDialog({ roles, role, onClose, onSaved }: { roles: Role[]; role?: Role; onClose: () => void; onSaved: (role: Role) => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(role?.label ?? '');
  const [copyFrom, setCopyFrom] = useState('');
  const save = useMutation({
    mutationFn: () =>
      role
        ? api<Role>(`/api/admin/roles/${role.id}`, { method: 'PATCH', body: { label: label.trim(), version: role.version } })
        : api<Role>('/api/admin/roles', { method: 'POST', body: { label: label.trim(), ...(copyFrom ? { copyFrom } : {}) } }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY });
      onSaved(saved);
    },
  });
  // owner 2026-10-23 21:14: Escape with a changed name asks first
  const guardRef = useUnsavedGuard({
    dirty: label !== (role?.label ?? '') || copyFrom !== '',
    save: async () => {
      if (label.trim() === '') return false;
      await save.mutateAsync();
      return true;
    },
  });
  const title = role ? 'Zmień nazwę roli' : 'Nowa rola';
  return (
    <DialogShell titleId="role-form-title" title={title} onClose={onClose} onSubmit={() => label.trim() !== '' && save.mutate()} busy={save.isPending}>
      {!role && <p className="muted">Nowa rola zaczyna bez uprawnień, chyba że skopiujesz je z innej roli. Potem ustawisz je w „Uprawnienia”.</p>}
      {save.error ? <ErrorLine error={save.error} /> : null}
      <label className="field" ref={guardRef}>
        <span>Nazwa roli</span>
        <input autoFocus required maxLength={100} value={label} onChange={(e) => setLabel(e.target.value)} autoComplete="off" placeholder="np. Super pracownik" />
      </label>
      {!role && (
        <label className="field">
          <span>Skopiuj uprawnienia z</span>
          <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
            <option value="">— bez uprawnień —</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending || label.trim() === ''}>
          {save.isPending ? 'Zapisywanie…' : role ? 'Zapisz' : 'Dodaj rolę'}
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </DialogShell>
  );
}

/** Confirmation before a role is deleted; a role somebody still has is refused and the people are named. */
export function DeleteRoleDialog({ role, onClose, onDeleted }: { role: Role; onClose: () => void; onDeleted: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/api/admin/roles/${role.id}?version=${role.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY });
      onDeleted();
    },
  });
  return (
    <DialogShell titleId="role-delete-title" title="Usunąć rolę?" onClose={onClose} onSubmit={() => remove.mutate()} busy={remove.isPending}>
      <p>„{role.label}” zniknie z listy ról razem ze swoimi uprawnieniami. W historii zmian zostaje wpis o usunięciu.</p>
      {remove.error ? <ErrorLine error={remove.error} /> : null}
      <div className="actions">
        <button className="btn primary" type="submit" autoFocus disabled={remove.isPending}>
          {remove.isPending ? 'Usuwanie…' : 'Usuń rolę'}
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </DialogShell>
  );
}
